import { useEffect, useRef, useState } from "react";
import { agentLoginUrl, getAgentUser, streamAgentReply } from "./agentClient";
import { useAuth } from "./context/AuthContext";

export interface AgentUser {
  name?: string;
  email?: string;
  jira_connected?: boolean;
  atlassianConnected?: boolean;
  atlassian_connected?: boolean;
  isAtlassianConnected?: boolean;
}

export function useJiraAgent() {
  const { firebaseUser } = useAuth();
  const [user, setUser] = useState<AgentUser | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef<string | null>(sessionStorage.getItem("agentSessionId"));

  useEffect(() => {
    console.log("[useJiraAgent] useEffect triggered. firebaseUser:", firebaseUser ? "Exists" : "Null");
    if (!firebaseUser) {
      setUser(null);
      return;
    }

    console.log("[useJiraAgent] Fetching Firebase ID token...");
    
    // Obtain the Firebase ID token to authenticate with the backend
    firebaseUser.getIdToken().then(token => {
      console.log("[useJiraAgent] Successfully retrieved token, calling getAgentUser...");
      getAgentUser(token).then((userData) => {
        console.log("[useJiraAgent] User data resolved from backend:", userData);
        // If the backend returns null/fails, we assume Atlassian is not connected.
        setUser(userData || { jira_connected: false });
      }).catch((err) => {
        console.error("[useJiraAgent] Failed to resolve user data:", err);
        setUser({ jira_connected: false });
      });
    }).catch(err => {
      console.error("[useJiraAgent] Failed to get Firebase token:", err);
      setUser(null);
    });
  }, [firebaseUser]);

  async function send(text: string, onUpdate: (chunk: string) => void) {
    console.log("[useJiraAgent] send() called. firebaseUser exists?", !!firebaseUser);
    if (!firebaseUser) return;
    
    setBusy(true);
    try {
      const token = await firebaseUser.getIdToken();
      console.log("[useJiraAgent] Initiating streamAgentReply...");
      const newId = await streamAgentReply({
        message: text,
        sessionId: sessionRef.current,
        token,
        onChunk: (chunk: string) => onUpdate(chunk),
        onDone: () => console.log("[useJiraAgent] stream done.")
      });
      if (newId) {
        sessionRef.current = newId;
        sessionStorage.setItem("agentSessionId", newId);
      }
    } catch (err: any) {
      console.error("[useJiraAgent] Error in send():", err);
      onUpdate(err.status === 401 ? "" : `Error: ${err.message}`);
    } finally {
      setBusy(false);
    }
  }

  const signIn = async () => {
    if (!firebaseUser) {
      console.warn("[useJiraAgent] Cannot sign in to Atlassian, firebaseUser is null");
      return;
    }
    const token = await firebaseUser.getIdToken();
    console.log("[useJiraAgent] Redirecting to Atlassian login with token");
    window.location.href = agentLoginUrl(token);
  };

  const isConnected = user?.jira_connected === true || user?.atlassianConnected === true || user?.atlassian_connected === true;
  console.log("[useJiraAgent] Current state -> user:", user, "| isConnected:", isConnected);

  return { user, busy, send, signIn, isConnected };
}
